import { AI_HELP_RESERVED_SPACE_CSS_VAR, AI_HELP_YARL_STYLES } from '@constants/layout'
import { useBusinessContext } from '@context'
import { MalwareScanNotice, attachmentScanBlock } from '../File/MalwareScanNotice'
import { fetchFile } from '@query'
import { callAll } from '@utils'
import { motion } from 'framer-motion'
import React, { cloneElement, JSXElementConstructor, ReactElement, useRef, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import { createPortal } from 'react-dom'
import { Spinner } from '@components/ui/Primitives'
import { FaTimes } from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'
import styled, { useTheme } from 'styled-components'
import { useModalEscape } from '../Modal/modalStack'

interface Props {
  children: ReactElement<any, string | JSXElementConstructor<any>>
  fileId: number
}

const ACTION_BAR_HEIGHT = '4rem'
const MAX_MODAL_HEIGHT = 1400
const MAX_MODAL_WIDTH = 1200

export const FilePreview: React.FC<Props> = ({ children, fileId }) => {
  const { slug } = useBusinessContext()
  const theme = useTheme()
  const rootRef = useRef<HTMLElement>(document.getElementById('modal-root'))
  const [isOpen, setOpen] = useState(false)
  useModalEscape(isOpen, () => setOpen(false))

  const { data: file, isLoading } = useQuery({
    queryKey: [slug, 'files', fileId],
    queryFn: () => fetchFile({ slug, fileId }),
    enabled: isOpen,
    refetchInterval: query =>
      attachmentScanBlock(query.state.data?.malware_scan_status) === 'pending' ? 2000 : false
  })

  const fileType = file?.type?.toLowerCase()
  const isImage = fileType?.includes('image') ?? false
  const isPdf = fileType === 'application/pdf'
  const fileSrc = file?.file
  const scanBlock = attachmentScanBlock(file?.malware_scan_status)
  const modalRoot = rootRef.current

  return (
    <>
      {cloneElement(children, {
        onClick: callAll(() => {
          setOpen(true)
        }, children.props.onClick)
      })}

      {isOpen &&
        isLoading &&
        modalRoot &&
        createPortal(
          <Backdrop onClick={() => setOpen(false)}>
            <LoadingSpinner>
              <Spinner size={32} color={theme.colors.neutralWhite} />
            </LoadingSpinner>
          </Backdrop>,
          modalRoot
        )}

      {isOpen &&
        !isLoading &&
        (scanBlock || (isPdf && fileSrc)) &&
        modalRoot &&
        createPortal(
          <Backdrop onClick={() => setOpen(false)}>
            <ActionBar>
              <CloseButton>
                <FaTimes />
              </CloseButton>
            </ActionBar>
            {scanBlock ? (
              <ScanMessage>
                <MalwareScanNotice status={file?.malware_scan_status} />
              </ScanMessage>
            ) : (
              fileSrc && <StyledEmbed src={fileSrc} />
            )}
          </Backdrop>,
          modalRoot
        )}

      {isImage && fileSrc && !scanBlock && (
        <Lightbox
          open={isOpen}
          close={() => setOpen(false)}
          slides={[{ src: fileSrc }]}
          controller={{ closeOnBackdropClick: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
          styles={AI_HELP_YARL_STYLES}
        />
      )}
    </>
  )
}

const StyledEmbed = styled.iframe`
  position: fixed;
  top: 50%;
  left: calc((100dvw - var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px)) / 2);
  transform: translate(-50%, -50%);
  width: ${MAX_MODAL_WIDTH}px;
  height: ${MAX_MODAL_HEIGHT}px;
  max-width: calc(
    100dvw - var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px) - ${ACTION_BAR_HEIGHT} -
      ${ACTION_BAR_HEIGHT}
  );
  max-height: calc(100dvh - ${ACTION_BAR_HEIGHT} - ${ACTION_BAR_HEIGHT});
  outline: none;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  border: none;
`

const Backdrop = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  z-index: 10000;
  position: fixed;
  top: 0;
  left: 0;
  right: var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px);
  height: 100%;
  background: ${({ theme }) => theme.colors.backdrop};
`

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const ScanMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(28rem, calc(100% - 2rem));
`

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  height: ${ACTION_BAR_HEIGHT};
  z-index: 10;
`

const CloseButton = styled.div`
  height: ${ACTION_BAR_HEIGHT};
  width: ${ACTION_BAR_HEIGHT};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralBlack}44;
  }

  svg {
    font-size: 2rem;
    color: white;
  }
`
